// Sendeplan: Werkzeugleiste in Gruppen (Tag · Planen · Bearbeiten · Ansicht), Stundenköpfe mit Uhr,
// Soll/Ist und Über-/Unterlänge, große Zeilen (Start, Länge, Typ/Kategorie-Farbe, Titel/Interpret,
// Intro/Rampe, Fix/VT/Tempo), laufende Zeile hervorgehoben und im Blick, Gesendetes gedimmt,
// nicht gefüllte Plätze deutlich markiert; Wellenform, Übergangs-Editor und Restzeit unten.

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { api, fmtMs, toast, useLoad, usePoll } from "../../api";
import { PlaysBanner } from "../widgets";
import { MiniWave, OnAirWave, laneColor } from "../../wave";
import { SegueDrawer } from "./Segue";
import { Empty, Field, Modal, Switch, Tip, useMe, useStatus } from "../../ui";
import { typeInfo } from "./Clocks";

const fmtClock = (iso: string, seconds = true) =>
  new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", ...(seconds ? { second: "2-digit" } : {}) });
const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const STATUS: Record<string, [string, string]> = {
  planned: ["", ""],
  sent: ["info", "geladen"],
  played: ["ok", "gesendet"],
  skipped: ["", "übersprungen"],
  missed: ["", "verpasst"],
  unfilled: ["bad", "nicht gefüllt"],
  marker: ["info", "Marker"],
};

function SongPicker({ title, onPick, onClose }: { title: string; onPick: (m: any) => void; onClose: () => void }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  useEffect(() => {
    const t = window.setTimeout(async () => setRows((await api(`/api/library?q=${encodeURIComponent(q)}`)).items), 250);
    return () => window.clearTimeout(t);
  }, [q]);
  return (
    <Modal title={title} onClose={onClose}>
      <input type="text" autoFocus placeholder="Titel oder Interpret" value={q} onChange={(e) => setQ(e.target.value)} />
      <div style={{ maxHeight: 380, overflowY: "auto", marginTop: 8 }}>
        {rows.map((s) => (
          <div key={s.id} className="row" style={{ padding: "5px 0", cursor: "pointer", borderBottom: "1px solid var(--line)" }} onClick={() => onPick(s)}>
            <span style={{ flex: 1 }}>{s.artist ? `${s.artist} – ` : ""}{s.title}</span>
            <span className="small muted">{s.categories.map((c: any) => c.name).join(", ")}</span>
            <span className="small">{fmtMs(s.duration_ms)}</span>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function Explain({ id, onClose, onPick }: { id: string; onClose: () => void; onPick: (mediaId: string) => void }) {
  const { data } = useLoad<any>(`/api/log/explain/${id}`);
  return (
    <Modal wide title="Auswahl erklären" onClose={onClose}>
      {!data ? "Rechne …" : (
        <div className="stack">
          <div>
            <b>{data.entry.artist} – {data.entry.title}</b> um {fmtClock(data.entry.planned_start)} · Kategorie {data.entry.category ?? "–"}
            {data.relaxed.length > 0 && <div className="banner warn" style={{ marginTop: 8 }}>Gelockert, weil sonst nichts übrig war: {data.relaxed.join(", ")}</div>}
          </div>
          <div className="grid g2">
            <div>
              <h3>Im Rennen ({data.ranked.length})</h3>
              <table className="list">
                <tbody>
                  {data.ranked.map((r: any, i: number) => (
                    <tr key={r.id} style={{ background: r.id === data.chosen ? "var(--ok-2)" : undefined }}>
                      <td className="small muted">{i + 1}.</td>
                      <td>{r.artist} – {r.title} {r.id === data.chosen && <span className="badge ok">gewählt</span>}</td>
                      <td className="small muted">Punkte {r.score}</td>
                      <td><button className="btn small" onClick={() => onPick(r.id)}>Nehmen</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <h3>Ausgeschlossen ({data.excluded.length})</h3>
              <table className="list">
                <tbody>
                  {data.excluded.map((r: any) => (
                    <tr key={r.id}><td>{r.artist} – {r.title}</td><td className="small" style={{ color: "var(--bad)" }}>{r.reason}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Shares({ onClose }: { onClose: () => void }) {
  const { data, reload } = useLoad<any[]>("/api/log/shares");
  return (
    <Modal title="Geteilte Links" onClose={onClose}>
      {!data?.length ? <Empty>Noch nichts geteilt.</Empty> : (
        <table className="list">
          <tbody>
            {data.map((s) => (
              <tr key={s.token}>
                <td className="small">{s.entry_id ? "Zeile" : `${String(s.day_from).slice(0, 10)} – ${String(s.day_to).slice(0, 10)}`}</td>
                <td className="small muted">bis {new Date(s.expires_at).toLocaleDateString("de-DE")} · {s.hits} Abrufe</td>
                <td>{s.revoked ? <span className="badge">widerrufen</span> : <a className="small" href={`/#/geteilt/${s.token}`} target="_blank" rel="noreferrer">öffnen</a>}</td>
                <td>{!s.revoked && <button className="btn small danger" onClick={async () => { await api(`/api/log/shares/${s.token}`, { method: "DELETE" }); reload(); }}>Widerrufen</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}

function Import({ day, onClose }: { day: string; onClose: (changed: boolean) => void }) {
  const [text, setText] = useState("");
  const [rows, setRows] = useState<any[] | null>(null);
  const run = async (apply: boolean) => {
    const r = await api("/api/log/import", { body: { day, text, apply } });
    setRows(r.rows);
    if (apply) {
      toast("Importiert");
      onClose(true);
    }
  };
  return (
    <Modal title={`Sendeplan importieren (${day})`} onClose={() => onClose(false)}>
      <p className="muted small" style={{ marginTop: 0 }}>Je Zeile „Zeit; Interpret; Titel“ (z. B. aus einer Tabelle oder dem CSV-Export). Gefundene Songs werden als Einträge zu ihrer Zeit eingefügt.</p>
      <textarea rows={8} value={text} onChange={(e) => { setText(e.target.value); setRows(null); }} placeholder={"06:00:00;Beispielband;Sommerlied"} />
      {rows && <div className="small" style={{ marginTop: 6 }}>{rows.filter((r) => !r.error).length} gefunden, {rows.filter((r) => r.error).length} nicht: {rows.filter((r) => r.error).slice(0, 5).map((r) => r.line).join(" · ")}</div>}
      <div className="row" style={{ marginTop: 10 }}><span className="spacer" /><button className="btn" onClick={() => run(false)} disabled={!text.trim()}>Prüfen</button><button className="btn primary" onClick={() => run(true)} disabled={!rows}>Importieren</button></div>
    </Modal>
  );
}

export function Log() {
  const me = useMe();
  const status = useStatus();
  const sampled = useRef({ np: null as any, at: performance.now() });
  const rawNp = status?.engine?.now_playing;
  // The engine's position is engine_age_ms old when the answer leaves the server: count it in (no lag behind the air).
  if (sampled.current.np !== rawNp) sampled.current = { np: rawNp, at: performance.now() - Math.min(3000, Number(status?.engine_age_ms ?? 0)) };
  const [tick, setTick] = useState(0);
  useEffect(() => { const timer = window.setInterval(() => setTick((x) => x + 1), 100); return () => window.clearInterval(timer); }, []);
  const np = rawNp ? { ...rawNp, pos_ms: Math.min((rawNp.item?.marks?.cue_in ?? 0) + rawNp.len_ms, rawNp.pos_ms + Math.min(5000, performance.now() - sampled.current.at) * (1 + (rawNp.item?.tempo_pct ?? 0) / 100)) } : null;
  const airProgress = np ? Math.max(0, Math.min(1, (np.pos_ms - (np.item?.marks?.cue_in ?? 0)) / Math.max(1, np.len_ms))) : 0;
  const canEdit = ["admin", "programm", "redaktion"].includes(me?.role);
  const [day, setDay] = useState(isoDay(new Date()));
  const [compact, setCompact] = useState(false);
  const [waves, setWavesState] = useState(() => { try { return localStorage.getItem("rc-log-waves") === "1"; } catch { return false; } });
  const setWaves = (v: boolean) => { setWavesState(v); try { localStorage.setItem("rc-log-waves", v ? "1" : "0"); } catch { /* egal */ } };
  const [segue, setSegue] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const { data, reload } = useLoad<any>(`/api/log?day=${day}${waves ? "&waves=1" : ""}`, [day, waves]);
  const { data: settings, reload: reloadSettings } = useLoad<any>("/api/log/settings");
  const { data: health } = usePoll<any>("/api/log/health", 30_000);
  const [menu, setMenu] = useState<{ x: number; y: number; e: any } | null>(null);
  const [picker, setPicker] = useState<{ mode: "replace" | "insert"; e: any } | null>(null);
  const [explain, setExplain] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const [shares, setShares] = useState(false);
  const [imp, setImp] = useState(false);
  const [gen, setGen] = useState(false);
  const [genDays, setGenDays] = useState(1);
  const [drag, setDrag] = useState<any | null>(null);
  const nowRef = useRef<HTMLDivElement | null>(null);

  // Refresh every 30 s (status of entries changes as they air).
  useEffect(() => {
    const t = window.setInterval(() => document.visibilityState === "visible" && reload(), 30_000);
    return () => window.clearInterval(t);
  }, [reload]);
  useEffect(() => {
    const close = () => setMenu(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);

  const byHour = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const e of data?.entries ?? []) m.set(e.hour_start, [...(m.get(e.hour_start) ?? []), e]);
    return m;
  }, [data]);
  const conflicts = (data?.entries ?? []).filter((e: any) => e.status === "unfilled" || (e.badges ?? []).some((b: string) => b.startsWith("gelockert"))).length;
  const nowIso = new Date().toISOString();
  const currentId = status?.engine?.now_playing?.item?.id;
  const playing = (data?.entries ?? []).find((e: any) => e.id === currentId);
  const nextUp = (data?.entries ?? []).filter((e: any) => e.status === "planned" && e.planned_start > nowIso).slice(0, 3);

  const act = async (fn: () => Promise<any>, ok: string) => {
    try {
      await fn();
      toast(ok);
      reload();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const put = (e: any, body: any, ok: string) => act(() => api(`/api/log/entry/${e.id}`, { method: "PUT", body }), ok);
  const share = async (body: any) => {
    const r = await api("/api/log/share", { body });
    const url = `${window.location.origin}${r.url}`;
    try {
      await navigator.clipboard.writeText(url);
      toast("Link kopiert (30 Tage gültig)");
    } catch {
      window.prompt("Link (30 Tage gültig)", url);
    }
  };
  const dropOn = async (target: any) => {
    if (!drag || drag.id === target.id || drag.hour_start !== target.hour_start) return setDrag(null);
    const list = (byHour.get(target.hour_start) ?? []).filter((x) => x.id !== drag.id);
    list.splice(list.findIndex((x) => x.id === target.id), 0, drag);
    setDrag(null);
    await act(() => api("/api/log/order", { method: "PUT", body: { ids: list.map((x) => x.id) } }), "Umsortiert");
  };
  const shiftDay = (n: number) => {
    const d = new Date(`${day}T12:00:00`);
    d.setDate(d.getDate() + n);
    setDay(isoDay(d));
  };
  const editingAir = (h: string) => new Date(h).getTime() <= Date.now() && Date.now() < new Date(h).getTime() + 3_600_000;

  const isToday = day === isoDay(new Date());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [openContainer, setOpenContainer] = useState<string | null>(null);
  const [openPast, setOpenPast] = useState<Record<string, boolean>>({});
  const setFixed = (e: any) => {
    const cur = e.fixed ? `${e.fixed === "hard" ? "hart" : "weich"} ${fmtClock(e.fix_at)}` : `weich ${fmtClock(e.planned_start)}`;
    const v = window.prompt("Fixzeit: „hart HH:MM:SS“ (ausblenden, sekundengenau), „weich HH:MM:SS“ (nach dem laufenden Titel) oder leer = keine", cur);
    if (v == null) return;
    const m = /^\s*(hart|weich)\s+(\d{1,2}:\d{2}(?::\d{2})?)\s*$/i.exec(v);
    if (v.trim() && !m) return toast("Bitte z. B. „weich 10:15:00“ oder „hart 10:30“", true);
    put(e, m ? { fixed: m[1].toLowerCase() === "hart" ? "hard" : "soft", fix_time: m[2] } : { fixed: "" }, m ? "Fixzeit gesetzt" : "Fixzeit entfernt");
  };
  const group = (title: string, children: React.ReactNode) => (
    <div className="row" style={{ gap: 6, padding: "0 10px", borderLeft: "1px solid var(--line)" }}>
      <span className="small muted" style={{ textTransform: "uppercase", letterSpacing: 0.6, fontSize: 10 }}>{title}</span>
      {children}
    </div>
  );
  // The running row stays in view (only when it changes, so scrolling by hand is not fought).
  useEffect(() => {
    if (isToday && currentId) window.setTimeout(() => nowRef.current?.scrollIntoView({ block: "center", behavior: "smooth" }), 200);
  }, [currentId, isToday]);
  const fs = compact ? 13 : 15;

  return (
    <div className="stack">
      <PlaysBanner health={health} />
      {/* Toolbar in groups: day/hour navigation · planning · editing · view */}
      <div className="card row wrap" style={{ padding: "8px 4px", gap: 0 }}>
        {group("Tag", <>
          <button className="btn small" onClick={() => shiftDay(-1)} title="Tag davor">←</button>
          <input type="date" style={{ width: 150 }} value={day} onChange={(e) => setDay(e.target.value)} />
          <button className="btn small" onClick={() => shiftDay(1)} title="Tag danach">→</button>
          <button className="btn small" onClick={() => { setDay(isoDay(new Date())); setTimeout(() => nowRef.current?.scrollIntoView({ block: "center" }), 300); }}>Jetzt</button>
          <select style={{ width: 96 }} value="" onChange={(e) => { const el = document.getElementById(`hour-${e.target.value}`); el?.scrollIntoView({ block: "start" }); }}>
            <option value="">Stunde …</option>{(data?.hours ?? []).map((h: any) => <option key={h.hour_start} value={h.local_hour}>{String(h.local_hour).padStart(2, "0")}:00</option>)}
          </select>
        </>)}
        {canEdit && group("Planen", <>
          <button className="btn small primary" onClick={() => setGen(true)}>Generieren …</button>
          <button className="btn small" title="Tauscht gleich lange Songs derselben Kategorie, damit weniger Regeln gelockert werden müssen" onClick={async () => { try { const r = await api("/api/log/optimize", { body: { day } }); toast(r.swaps ? `${r.swaps} Tausche – Konflikte ${r.before} → ${r.after}` : `Nichts zu verbessern (${r.before} Konflikte)`); reload(); } catch (e: any) { toast(e.message, true); } }}>Optimieren</button>
          <button className="btn small" onClick={() => act(() => api("/api/log/retime", { body: { day } }), "Neu getimt")}>Neu timen</button>
        </>)}
        {canEdit && group("Bearbeiten", <>
          <button className="btn small" onClick={(e) => { e.stopPropagation(); setMore(!more); }}>Mehr ▾</button>
        </>)}
        {group("Ansicht", <>
          <Switch checked={compact} onChange={setCompact} label="Kompakt" />
          <Switch checked={waves} onChange={setWaves} label="Wellenform" />
          <button className="btn small" onClick={reload} title="Aktualisieren">⟳</button>
          {canEdit && <button className="btn small" title="Einstellungen des Sendeplans" onClick={(e) => { e.stopPropagation(); setSettingsOpen(!settingsOpen); }}>⚙</button>}
        </>)}
        <span className="spacer" />
        <span className={`badge ${conflicts ? "warn" : "ok"}`} style={{ marginRight: 8 }}>{conflicts ? `${conflicts} offen/gelockert` : "keine Konflikte"}</span>
      </div>
      {more && (
        <div className="card row wrap" style={{ padding: 8 }} onClick={(e) => e.stopPropagation()}>
          <button className="btn small" onClick={() => window.confirm("Tag neu aufbauen? Handänderungen und VT-Slots in diesem Tag gehen verloren (Gesendetes und Gesperrtes bleibt).") && act(() => api("/api/log/generate", { body: { day, hours: 24, replace: true } }), "Neu aufgebaut")}>Tag neu aufbauen</button>
          <button className="btn small danger" onClick={() => window.confirm("Ungesendete Einträge dieses Tages löschen?") && act(() => api("/api/log/clear", { body: { day } }), "Geleert")}>Tag leeren</button>
          <span style={{ width: 1, alignSelf: "stretch", background: "var(--line)" }} />
          <button className="btn small" onClick={() => setImp(true)}>⇪ Importieren</button>
          <a className="btn small" href={`/api/log/export?day=${day}`}>⇩ Exportieren (CSV)</a>
          <span style={{ width: 1, alignSelf: "stretch", background: "var(--line)" }} />
          <button className="btn small" onClick={() => share({ day_from: day, day_to: day })}>Teilen (Nur-Lese-Link)</button>
          <button className="btn small" onClick={() => setShares(true)}>Geteilte Links</button>
        </div>
      )}
      {settingsOpen && settings && (
        <div className="card row wrap" style={{ padding: 8, gap: 12 }}>
          <span className="small">Voraus planen</span>
          <select style={{ width: 120 }} value={settings.autofill_hours} onChange={async (e) => { await api("/api/log/settings", { method: "PUT", body: { autofill_hours: Number(e.target.value) } }); reloadSettings(); }}>
            {[1, 6, 24, 72, 168, 336].map((h) => <option key={h} value={h}>{h < 24 ? `${h} h` : `${h / 24} Tage`}</option>)}
          </select>
          <Switch checked={settings.use_log} onChange={async (x) => { await api("/api/log/settings", { method: "PUT", body: { use_log: x } }); reloadSettings(); }} label={settings.use_log ? "Engine spielt den Sendeplan" : "Engine spielt Rotation"} />
        </div>
      )}
      {/* Day bar: one block per hour, coloured by fill. */}
      <div className="row" style={{ gap: 2 }}>
        {(data?.hours ?? []).map((h: any) => {
          const past = new Date(h.hour_start).getTime() + 3_600_000 < Date.now();
          const ok = h.entries && Math.abs(h.deviation_ms ?? 0) < 120_000 && !h.unfilled;
          return <a key={h.hour_start} href={`#hour-${h.local_hour}`} onClick={(e) => { e.preventDefault(); document.getElementById(`hour-${h.local_hour}`)?.scrollIntoView({ block: "start" }); }}
            title={`${h.local_hour}:00 – ${h.entries ? `${fmtMs(h.fill_ms)} gefüllt` : "leer"}`}
            style={{ flex: 1, height: 18, borderRadius: 4, background: !h.entries ? "#e2ded4" : ok ? "var(--ok)" : "var(--warn)", opacity: past ? 0.4 : 1, outline: editingAir(h.hour_start) ? "2px solid var(--live)" : undefined, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#fff", textDecoration: "none" }}>{h.local_hour % 3 === 0 ? h.local_hour : ""}</a>;
        })}
      </div>
      {isToday && (
        <div className="card row wrap" style={{ gap: 16, borderLeft: "5px solid var(--live)" }}>
          <span className="badge live">ON AIR</span>
          {playing ? <b style={{ fontSize: 16 }}>{playing.artist ? `${playing.artist} – ` : ""}{playing.title}</b> : <span className="muted">{status?.engine?.now_playing ? `${status.engine.now_playing.item.artist} – ${status.engine.now_playing.item.title} (aus der Rotation)` : "–"}</span>}
          <span className="spacer" />
          <span className="small muted">Als Nächstes:</span>
          {nextUp.map((e: any) => <span key={e.id} className="small">{fmtClock(e.planned_start, false)} {e.artist ? `${e.artist} – ` : ""}{e.title || e.label}</span>)}
        </div>
      )}
      {settings && !settings.use_log && <div className="banner warn">Die Engine spielt gerade die Rotation, nicht diesen Sendeplan (⚙ › „Engine spielt den Sendeplan“).</div>}
      {!data ? <div className="card">Lade …</div> : !data.entries.length ? (
        <Empty>Für diesen Tag ist noch nichts geplant. {canEdit && <a href="#" onClick={(e) => { e.preventDefault(); setGen(true); }}>Jetzt generieren</a>} – dafür braucht es Uhren im Wochenraster.</Empty>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {data.hours.map((h: any) => {
            const list = byHour.get(h.hour_start) ?? [];
            const dev = h.deviation_ms ?? 0;
            const good = Math.abs(dev) < 120_000;
            const past = new Date(h.hour_start).getTime() + 3_600_000 < Date.now();
            const startRow = list.find((x: any) => x.fixed && x.fix_at && new Date(x.fix_at).getTime() === new Date(h.hour_start).getTime());
            const lockS = Math.round((startRow?.fix_lock_ms ?? 60_000) / 1000), overS = Math.round((startRow?.fix_overrun_ms ?? 60_000) / 1000);
            const mmss = (s: number) => `:${String(Math.floor((3600 + s) / 60) % 60).padStart(2, "0")}${(3600 + s) % 60 ? `:${String((3600 + s) % 60).padStart(2, "0")}` : ""}`;
            const collapsed = past && isToday && !openPast[h.hour_start];
            return (
              <div key={h.hour_start} id={`hour-${h.local_hour}`}>
                <div className="row wrap" style={{ padding: "10px 14px", background: editingAir(h.hour_start) ? "#fdeee8" : "#f3f1ea", borderBottom: "1px solid var(--line)", borderTop: "1px solid var(--line)", position: "sticky", top: 0, zIndex: 2, gap: 12, opacity: past ? 0.75 : 1 }}>
                  <span style={{ fontSize: 30, fontWeight: 800, letterSpacing: -1, lineHeight: 1, minWidth: 56, color: "#2b3a34" }}>{String(h.local_hour).padStart(2, "0")}<span style={{ fontSize: 13, fontWeight: 600, opacity: 0.55 }}>:00</span></span>
                  {h.clock ? <span className="badge" style={{ background: h.clock.color, color: "#fff", fontSize: 12 }}>{h.clock.name}</span> : <span className="badge warn">keine Uhr</span>}
                  {h.entries > 0 && (
                    <span className="small" style={{ fontVariantNumeric: "tabular-nums" }}>
                      Soll <b>60:00</b> · Ist <b>{fmtMs(h.fill_ms)}</b> · <b style={{ color: good ? "var(--ok)" : "var(--warn)" }}>{dev >= 0 ? "+" : "−"}{fmtMs(Math.abs(dev))}</b> {dev > 0 ? "zu lang" : dev < 0 ? "zu kurz" : ""}
                    </span>
                  )}
                  {startRow && <span className="badge" style={{ background: startRow.fixed === "hard" ? "#c8382f" : "#2f6fb1", color: "#fff" }}
                    title={startRow.fixed === "hard" ? "Fester Stundenbeginn: zur vollen Stunde wird ausgeblendet, die Stunde startet sekundengenau" : `Weicher Stundenwechsel: ab ${mmss(-lockS)} startet nichts mehr aus der Vorstunde; ein Titel startet nur, wenn er bis ${mmss(overS)} fertig ist`}>
                    {startRow.fixed === "hard" ? "Beginn hart :00" : `weich · Sperre ab ${mmss(-lockS)} · bis ${mmss(overS)}`}</span>}
                  {startRow?.actual_start && <span className="small muted" title="Tatsächlicher Stundenwechsel">Wechsel um <b className="mono">{fmtClock(startRow.actual_start)}</b></span>}
                  {h.unfilled > 0 && <span className="badge bad">{h.unfilled} nicht gefüllt</span>}
                  {h.relaxed > 0 && <span className="badge warn">{h.relaxed} gelockert</span>}
                  {editingAir(h.hour_start) && <span className="badge live">läuft</span>}
                  <span className="spacer" />
                  {canEdit && !past && <button className="btn small" onClick={() => (!editingAir(h.hour_start) || window.confirm("Diese Stunde läuft gerade – Hörer merken das. Trotzdem neu generieren? Gesendetes bleibt.")) && act(() => api("/api/log/hour", { body: { hour_start: h.hour_start } }), "Stunde neu generiert")}>Stunde neu planen</button>}
                </div>
                {!list.length && <div className="small muted" style={{ padding: "8px 14px" }}>keine Einträge{!h.clock ? " (keine Uhr im Wochenraster)" : ""}</div>}
                {collapsed && list.length > 0 && <div className="small muted click" style={{ padding: "6px 14px", cursor: "pointer" }} onClick={() => setOpenPast({ ...openPast, [h.hour_start]: true })}>▸ {list.filter((x: any) => x.status === "played").length} Titel gespielt · anzeigen</div>}
                {!collapsed && list.map((e) => {
                  const [cls, label] = STATUS[e.status] ?? ["", e.status];
                  const isNow = e.id === currentId;
                  const info = typeInfo(e.type);
                  const played = e.status === "played" || ["skipped", "missed"].includes(e.status);
                  const unfilled = e.status === "unfilled";
                  const color = e.category_color ?? info.color;
                  const introS = e.intro != null && e.intro > (e.cue_in ?? 0) ? Math.round((e.intro - (e.cue_in ?? 0)) / 1000) : null;
                  const badges: string[] = e.badges ?? [];
                  const ramp = badges.find((b) => b.startsWith("Drop-in auf der Rampe") || b.startsWith("auf der Rampe"));
                  const isVt = e.type === "voicetrack";
                  // Verbund: ad and news slots are placeholders every taking-over station fills with its own.
                  const slot = unfilled && ["ad", "news"].includes(e.type) && e.duration_ms > 0;
                  const fixLabel = e.fixed && e.fix_at ? `${e.fixed === "hard" ? "🔒 hart" : "⏱ weich"} ${fmtClock(e.fix_at)}` : null;
                  // Empty slots without air time (VT not recorded, packaging missing) are skipped on air: a quiet line only.
                  if (unfilled && !(e.duration_ms > 0)) return (
                    <div key={e.id} className="small" style={{ padding: "1px 14px 1px 196px", color: "var(--ink-3)", borderBottom: "1px dashed var(--line)", fontSize: 11.5, background: "#fbfaf7" }}
                      onContextMenu={(ev) => { if (!canEdit) return; ev.preventDefault(); setMenu({ x: ev.clientX, y: ev.clientY, e }); }}>
                      {isVt ? "VT-Platz frei – wird übersprungen" : `${e.label || info.label}: ${(badges[0] ?? "leer").replace(/^keine eigene Verpackung \((.*)\) – übersprungen$/, "keine eigene Verpackung ($1) – wird übersprungen")}`}
                      {isVt && <a href="#/plan/voicetracking" style={{ marginLeft: 8 }}>aufnehmen →</a>}
                    </div>
                  );
                  return (
                    <Fragment key={e.id}>
                    <div ref={isNow ? nowRef : undefined}
                      draggable={canEdit && !["played", "sent"].includes(e.status)}
                      onDragStart={() => setDrag(e)} onDragOver={(ev) => ev.preventDefault()} onDrop={() => dropOn(e)}
                      onContextMenu={(ev) => { if (!canEdit) return; ev.preventDefault(); setMenu({ x: ev.clientX, y: ev.clientY, e }); }}
                      style={{ display: "grid", gridTemplateColumns: `86px 54px 30px minmax(0,1fr) auto`, alignItems: "center", gap: 10,
                        padding: compact ? "3px 14px" : "8px 14px", borderBottom: "1px solid var(--line)", borderLeft: `${isNow ? 8 : 5}px solid ${isNow ? "var(--live)" : color}`,
                        background: isNow ? "linear-gradient(90deg,#fbd9cf,#fdf0eb)" : unfilled && !slot ? "repeating-linear-gradient(135deg,#fdeceb,#fdeceb 8px,#fbe2e0 8px,#fbe2e0 16px)" : e.type === "ad" ? "#fdf3e7" : e.type === "news" ? "#eaf5f2" : undefined,
                        opacity: played && !isNow ? 0.5 : 1, fontSize: fs }}>
                      <span title={e.actual_start ? "gespielt um" : "geplant um"} style={{ lineHeight: 1.05 }}>
                        <span className="mono" style={{ fontSize: fs + 1, fontWeight: isNow ? 700 : 500 }}>{fmtClock(e.actual_start ?? e.planned_start)}</span>
                        <span style={{ display: "block", fontSize: 10, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".04em" }}>{e.actual_start ? (isNow ? "seit" : "gespielt") : e.status === "skipped" ? "ausgelassen" : "geplant"}</span>
                      </span>
                      <span className="mono small" style={{ textAlign: "right", opacity: 0.8 }}>{fmtMs(e.duration_ms)}</span>
                      <span title={info.label} style={{ width: 26, height: 26, borderRadius: 7, background: color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>{info.icon}</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {slot ? <b>{e.label}</b> : unfilled ? <b style={{ color: "var(--bad)" }}>nicht gefüllt</b> : <b style={{ textDecoration: e.status === "skipped" ? "line-through" : undefined }}>{e.title || e.label || info.label}</b>}
                          {unfilled && <span className="muted"> · {e.label || info.label}</span>}
                          {e.artist && !unfilled && e.artist !== e.title && <span className="muted"> · {e.artist}</span>}
                          {fixLabel && <span className="badge" title="Fixzeit" style={{ marginLeft: 8, background: e.fixed === "hard" ? "#fbe0dd" : "#e1ecf8", color: e.fixed === "hard" ? "#c8382f" : "#2f6fb1" }}>{fixLabel}</span>}
                          {e.container_parts && <button className="btn small" style={{ marginLeft: 8, padding: "0 6px" }} onClick={() => setOpenContainer(openContainer === e.id ? null : e.id)}>Container · {e.container_parts.length} Teile {openContainer === e.id ? "▴" : "▾"}</button>}
                        </div>
                        {!compact && (
                          <div className="row wrap" style={{ gap: 5, marginTop: 3, fontSize: 12 }}>
                            {introS != null && <span style={{ color: "#2f6fb1" }}>Intro {introS} s</span>}
                            {ramp && <span className="badge" style={{ background: "#e8673c", color: "#fff" }}>{ramp}</span>}
                            {isVt && <span className="badge accent">VT</span>}
                            {e.locked && <span className="badge" title="gesperrt – bleibt beim Neuplanen">Fix</span>}
                            {(e.tempo_pct ?? 0) !== 0 && <span className="badge info">Tempo {e.tempo_pct > 0 ? "+" : ""}{e.tempo_pct} %</span>}
                            {e.category && <span className="badge" style={{ background: `${e.category_color}22`, color: e.category_color }}>{e.category}</span>}
                            {e.candidates != null && <span className="muted">{e.candidates} im Rennen</span>}
                            {badges.filter((b) => b !== ramp && b !== "VT aufnehmen" && !b.startsWith("Tempo")).map((b) => <span key={b} className={`badge ${b.startsWith("gelockert") || b === "Regeln gelockert" ? "warn" : b === "nicht gefüllt" || b.startsWith("Kategorie leer") ? "bad" : "info"}`}>{b}</span>)}
                            {badges.includes("VT aufnehmen") && <a className="badge accent" href="#/plan/voicetracking">VT aufnehmen →</a>}
                            {(e.trans_in || e.trans_out) && <span className="muted">Übergang {e.trans_in || "–"}/{e.trans_out || "–"}</span>}
                          </div>
                        )}
                      </div>
                      <div className="row" style={{ gap: 8 }}>
                        {isNow ? <span className="badge" style={{ background: "var(--live)", color: "white" }}>LÄUFT · −{fmtMs(Math.max(0, (np?.len_ms ?? 0) - ((np?.pos_ms ?? 0) - (np?.item?.marks?.cue_in ?? 0))))}</span> : label && !unfilled && <span className={`badge ${cls}`}>{label}</span>}
                        {e.preview_available && e.news_source_id && ["news", "weather"].includes(e.type) && <audio controls preload="none" src={`/api/${e.type === "weather" ? "weather" : "news"}/${e.news_source_id}/preview`} aria-label={`${e.type === "weather" ? "Wetter" : "Nachrichten"} ${e.title || e.label} mit Bett vorhören`} title="Beitrag mit dem eingestellten Musikbett vorhören" style={{ width: 220, height: compact ? 26 : 30 }} />}
                        {waves && e.waveform && (
                          <span title="Klick: groß anzeigen" style={{ cursor: "pointer" }} onClick={() => setExpanded(expanded === e.id ? null : e.id)}>
                            <MiniWave peaks={e.waveform} color={laneColor(e.type)} width={160} height={compact ? 16 : 30}
                              progress={isNow && status?.engine?.now_playing ? Math.min(1, np.pos_ms / Math.max(1, e.media_ms ?? ((np.item?.marks?.cue_in ?? 0) + np.len_ms))) : undefined} />
                          </span>
                        )}
                        {canEdit && e.media_id && !["played", "sent"].includes(e.status) && <button className="iconbtn" title="Übergang mit Wellenform bearbeiten" onClick={() => setSegue(e.id)}>⇄</button>}
                      </div>
                    </div>
                    {isNow && <div role="progressbar" aria-label="Läuft" aria-valuenow={Math.round(airProgress * 100)} style={{ height: 4, background: "#fbd9cf" }}><div style={{ height: "100%", width: `${airProgress * 100}%`, background: "var(--live)" }} /></div>}
                    {e.container_parts && openContainer === e.id && (
                      <div style={{ padding: "4px 14px 8px 196px", borderBottom: "1px solid var(--line)", background: "#f4f0fb" }}>
                        {e.container_parts.map((p: any, i: number) => (
                          <div key={i} className="row small" style={{ gap: 10, padding: "2px 0" }}>
                            <span className="mono muted" style={{ width: 52 }}>{fmtMs(p.duration_ms)}</span>
                            <span>{p.kind === "silence" ? "⎵" : "♪"} {p.title}{p.artist ? ` · ${p.artist}` : ""}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {expanded === e.id && e.waveform && (
                      <div style={{ padding: "6px 14px 10px 196px", borderBottom: "1px solid var(--line)", background: "#f7f5ef" }}>
                        <ExpandedWave e={e} />
                      </div>
                    )}
                    </Fragment>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
      <Tip>Rechtsklick auf eine Zeile: Ersetzen, Danach einfügen, VT hier, Auswahl erklären, Überspringen, Sperren (Fix), Übergang, Zeile teilen. Zeilen innerhalb einer Stunde ziehen sortiert um.</Tip>
      {menu && (
        <div className="card" style={{ position: "fixed", left: menu.x, top: menu.y, zIndex: 60, padding: 4, minWidth: 200 }} onClick={(e) => e.stopPropagation()}>
          {[
            ["Ersetzen …", () => setPicker({ mode: "replace", e: menu.e })],
            ["Danach einfügen …", () => setPicker({ mode: "insert", e: menu.e })],
            ["VT hier", () => act(() => api("/api/log/insert", { body: { after_id: menu.e.id } }), "Voicetrack-Slot eingefügt")],
            ...(menu.e.media_id || menu.e.status === "unfilled" ? [["Auswahl erklären", () => setExplain(menu.e.id)] as [string, () => void]] : []),
            [menu.e.status === "skipped" ? "Wieder aufnehmen" : "Überspringen", () => put(menu.e, menu.e.status === "skipped" ? { unskip: true } : { skip: true }, "Geändert")],
            [menu.e.locked ? "Entsperren" : "Sperren (bleibt beim Neuplanen)", () => put(menu.e, { locked: !menu.e.locked }, "Geändert")],
            [menu.e.fixed ? "Fixzeit ändern …" : "Fixzeit setzen …", () => setFixed(menu.e)],
            ...(menu.e.media_id ? [["Übergang mit Wellenform …", () => setSegue(menu.e.id)] as [string, () => void]] : []),
            ["Übergang In/Out …", () => { const v = window.prompt("Übergang In / Out (z. B. kalt/blende)", `${menu.e.trans_in}/${menu.e.trans_out}`); if (v != null) { const [i, o] = v.split("/"); put(menu.e, { trans_in: (i ?? "").trim(), trans_out: (o ?? "").trim() }, "Übergang gesetzt"); } }],
            ...(menu.e.media_id ? [["Zeile zum Anhören teilen", () => share({ day_from: day, day_to: day, entry_id: menu.e.id })] as [string, () => void]] : []),
          ].map(([l, f]) => (
            <div key={l as string} className="row" style={{ padding: "6px 10px", cursor: "pointer", borderRadius: 6 }} onMouseDown={(ev) => ev.preventDefault()} onClick={() => { (f as () => void)(); setMenu(null); }}>{l as string}</div>
          ))}
        </div>
      )}
      {picker && (
        <SongPicker title={picker.mode === "replace" ? "Song ersetzen durch …" : "Danach einfügen …"} onClose={() => setPicker(null)}
          onPick={(m) => {
            const e = picker.e;
            setPicker(null);
            if (picker.mode === "replace") put(e, { media_id: m.id }, "Ersetzt");
            else act(() => api("/api/log/insert", { body: { after_id: e.id, media_id: m.id } }), "Eingefügt");
          }} />
      )}
      {explain && <Explain id={explain} onClose={() => setExplain(null)} onPick={(mid) => { const e = data.entries.find((x: any) => x.id === explain); setExplain(null); if (e) put(e, { media_id: mid }, "Ersetzt"); }} />}
      {day === isoDay(new Date()) && status?.engine?.now_playing && !segue && (
        <div style={{ position: "sticky", bottom: 8, zIndex: 20 }}><OnAirWave np={np} /></div>
      )}
      {segue && <SegueDrawer entryId={segue} onClose={() => setSegue(null)} onSaved={reload} />}
      {shares && <Shares onClose={() => setShares(false)} />}
      {imp && <Import day={day} onClose={(c) => { setImp(false); if (c) reload(); }} />}
      {gen && (
        <Modal title="Sendeplan generieren" onClose={() => setGen(false)}>
          <p className="muted small" style={{ marginTop: 0 }}>Plant ab {day} aus Wochenraster und Uhren. Bestehende Stunden bleiben, außer du wählst „neu aufbauen“. Gesendetes und Gesperrtes bleibt immer.</p>
          <div className="form">
            <Field label="Zeitraum">
              <select value={genDays} onChange={(e) => setGenDays(Number(e.target.value))}>{[1, 2, 3, 7, 14].map((d) => <option key={d} value={d}>{d} {d === 1 ? "Tag" : "Tage"}</option>)}</select>
            </Field>
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            <span className="spacer" />
            <button className="btn" onClick={async () => { setGen(false); await act(async () => { const r = await api("/api/log/generate", { body: { day, days: genDays, replace: false } }); if (r.background) toast("Läuft im Hintergrund – gleich aktualisieren"); }, "Fehlende Stunden geplant"); }}>Nur Lücken füllen</button>
            <button className="btn primary" onClick={async () => { setGen(false); await act(async () => { const r = await api("/api/log/generate", { body: { day, days: genDays, replace: true } }); if (r.background) toast("Läuft im Hintergrund – gleich aktualisieren"); }, "Neu aufgebaut"); }}>Neu aufbauen</button>
          </div>
        </Modal>
      )}
    </div>
  );
}


/** Wide waveform strip of one log row with cue marks and a small player. */
function ExpandedWave({ e }: { e: any }) {
  const box = useRef<HTMLDivElement>(null);
  const audio = useRef<HTMLAudioElement>(new Audio());
  const [w, setW] = useState(700);
  const [pos, setPos] = useState<number | null>(null);
  const [loop, setLoop] = useState(false);
  useEffect(() => {
    setW(box.current?.clientWidth ?? 700);
    const a = audio.current;
    const tick = () => setPos(a.currentTime * 1000);
    a.addEventListener("timeupdate", tick);
    return () => { a.pause(); a.removeEventListener("timeupdate", tick); };
  }, []);
  const dur = e.media_ms || 1;
  const x = (ms: number) => (ms / dur) * w;
  const playFrom = (ms: number, until?: number) => {
    const a = audio.current;
    if (!a.src.endsWith(`/api/media/${e.media_id}/audio`)) a.src = `/api/media/${e.media_id}/audio`;
    const go = () => { a.currentTime = ms / 1000; a.play().catch(() => {}); };
    if (a.readyState >= 1) go(); else a.addEventListener("loadedmetadata", go, { once: true });
    a.ontimeupdate = () => { if (until != null && a.currentTime * 1000 >= until) { if (loop) a.currentTime = ms / 1000; else a.pause(); } };
  };
  const intro = e.intro, seg = e.start_next ?? e.cue_out;
  return (
    <div ref={box}>
      <svg width={w} height={60} style={{ display: "block", cursor: "pointer" }} onClick={(ev) => { const r = (ev.currentTarget as SVGElement).getBoundingClientRect(); playFrom(((ev.clientX - r.left) / w) * dur); }}>
        <MiniWaveSvg peaks={e.waveform} w={w} h={60} color={laneColor(e.type)} />
        {e.cue_in != null && <line x1={x(e.cue_in)} x2={x(e.cue_in)} y1={0} y2={60} stroke="#2f9a63" strokeWidth={2} />}
        {intro != null && <line x1={x(intro)} x2={x(intro)} y1={0} y2={60} stroke="#2f6fb1" strokeWidth={2} />}
        {seg != null && <line x1={x(seg)} x2={x(seg)} y1={0} y2={60} stroke="#e8673c" strokeWidth={2} strokeDasharray="4 3" />}
        {pos != null && <line x1={x(pos)} x2={x(pos)} y1={0} y2={60} stroke="#111" strokeWidth={2} />}
      </svg>
      <div className="row small" style={{ gap: 6, marginTop: 4 }}>
        <button className="btn small" onClick={() => playFrom(e.cue_in ?? 0)}>▶ Start</button>
        {intro != null && <button className="btn small" onClick={() => playFrom(Math.max(0, intro - 5000), intro + 1000)}>▶ Intro-Ende</button>}
        {seg != null && <button className="btn small" onClick={() => playFrom(Math.max(0, seg - 8000), seg + 2000)}>▶ Ausklang</button>}
        <button className="btn small" onClick={() => audio.current.pause()}>■</button>
        <label className="row small"><input type="checkbox" checked={loop} onChange={(ev) => setLoop(ev.target.checked)} /> Schleife</label>
        <span className="muted">Klick in die Wellenform spielt ab dieser Stelle.</span>
      </div>
    </div>
  );
}

function MiniWaveSvg({ peaks, w, h, color }: { peaks: number[]; w: number; h: number; color: string }) {
  const n = peaks.length || 1;
  const bars = peaks.map((p, i) => <rect key={i} x={(i / n) * w} width={Math.max(1, w / n - 0.5)} y={h / 2 - (p * h) / 2} height={Math.max(1, p * h)} fill={color} opacity={0.8} />);
  return <g>{bars}</g>;
}
